import PropTypes from "prop-types";
import { forwardRef, useEffect, useState } from "react";
import { Link, useLocation, matchPath } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Collapse, List } from "@mui/material";

// icons
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { CodepenOutlined } from "@ant-design/icons";

// redux
import { useDispatch, useSelector } from "react-redux";
import { OpenNavGroup } from "../../../../../redux/slices/User/usersSlice";

// project imports
import { handlerActiveItem, useGetMenuMaster } from "../../../../../api/menu";

export default function CollapseNavItem({
  item,
  level,
  userRoleRouters,
  setOpenCollapseId,
  openCollapseId,
  open,
  setOpen,
  openDropDown,
  setOpenDropDown,
}) {
  const theme = useTheme();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const openItem = menuMaster.openedItem;
  const dispatch = useDispatch();

  // Handle item target
  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  // Set link properties
  let listItemProps = {
    component: forwardRef((props, ref) => (
      <Link
        underline="none"
        ref={ref}
        {...props}
        to={item.url}
        target={itemTarget}
      />
    )),
  };
  if (item?.external) {
    listItemProps = { component: "a", href: item.url, target: itemTarget };
  }

  // Item icon
  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon style={{ fontSize: drawerOpen ? "1rem" : "1.25rem" }} />
  ) : (
    false
  );

  // Check if item is selected
  const { pathname } = useLocation();
  const isSelected =
    !!matchPath({ path: item.url, end: false }, pathname) ||
    openItem === item.id;

  // Set active item on page load
  useEffect(() => {
    if (pathname === item.url) handlerActiveItem(item.id);
    // eslint-disable-next-line
  }, [pathname]);

  // Style variables
  const textColor = theme.palette.common.white;
  const iconSelectedColor = theme.palette.common.white;

  // Navigation state
  const [openGroup, setOpenGroup] = useState();
  const selectedNav = useSelector((state) => state.users.navGroup);

  // Handle click on navigation item
  const handleClick = (e) => {
    setOpenGroup(e?.id);
    setOpen(item.id == selectedNav && open ? false : true);
    setOpenDropDown((prev) => (prev === item.id ? null : item.id));
    dispatch(OpenNavGroup(e?.id));
  };

  // Render child routes
  function printRoutes(routeGroup) {
    const groupData = userRoleRouters?.map((group, i) => {
      if (group?.routeGroup === routeGroup.id && item.id == selectedNav) {
        return (
          <Collapse
            in={item.id == selectedNav && open}
            timeout="auto"
            unmountOnExit
            key={i}
          >
            <List component="div" disablePadding>
              {group.url?.map((userRoute, index) => {
                // Find matching child item
                const subMenu = item.children.find(
                  (child) => child.url === userRoute.url && child?.type === "item"
                );

                if (subMenu) {
                  // Determine display title based on childType
                  const displayTitle = subMenu.childType 
                    ? subMenu.childName || subMenu.title 
                    : subMenu.title;

                  // Check if current route is active
                  const isActive = 
                    window.location.pathname === subMenu.url ||
                    window.location?.pathname?.split("/")?.[2] ===
                      subMenu?.url?.split("/")?.[2];

                  return (
                    <Link
                      key={index}
                      to={subMenu.url}
                      style={{ textDecoration: "none" }}
                      onClick={() => window.location.pathname === subMenu.url}
                    >
                      <ListItemButton
                        key={index}
                        style={{
                          color: iconSelectedColor,
                          ...(isActive && {
                            backgroundColor: "#0b30f087",
                          }),
                        }}
                        sx={{
                          zIndex: 1201,
                          pl: drawerOpen ? `${level * 28}px` : 1.5,
                          py: !drawerOpen && level === 1 ? 1.25 : 1,
                          ...(drawerOpen && {
                            "&:hover": {
                              bgcolor: "#0b30f087",
                              color: iconSelectedColor,
                            },
                            "&.Mui-selected": {
                              borderRight: `2px solid ${theme.palette.primary.main}`,
                              bgcolor: "#0b30f087",
                              color: iconSelectedColor,
                              "&:hover": {
                                color: iconSelectedColor,
                                bgcolor: "primary.lighter",
                              },
                            },
                          }),
                          ...(!drawerOpen && {
                            "&:hover": {
                              bgcolor: "transparent",
                            },
                            "&.Mui-selected": {
                              "&:hover": {
                                bgcolor: "transparent",
                              },
                              bgcolor: "transparent",
                            },
                          }),
                        }}
                      >
                        <ListItemText primary={displayTitle} />
                      </ListItemButton>
                    </Link>
                  );
                }
                return null;
              })}
            </List>
          </Collapse>
        );
      }
      return null;
    });

    return groupData;
  }

  // Check if current group is selected
  const isGroupSelected = 
    item?.url?.split("/")?.[1]?.toLowerCase() ===
    window.location?.pathname?.split("/")?.[1]?.toLowerCase();

  return (
    <>
      <ListItemButton
        onClick={() => {
          handleClick(item);
          setOpenDropDown(item.id);
          setOpenCollapseId(item.id);
        }}
        disabled={item.disabled}
        selected={isGroupSelected}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(isGroupSelected && {
            "&:hover": {
              bgcolor: "#0b30f087",
            },
            "&.Mui-selected": {
              bgcolor: "#0b30f087",
              borderRight: `2px solid ${theme.palette.primary.main}`,
              color: iconSelectedColor,
              "&:hover": {
                color: iconSelectedColor,
                bgcolor: "#0b30f087",
              },
            },
          }),
        }}
      >
        <ListItemIcon style={{ color: iconSelectedColor }}>
          {itemIcon}
        </ListItemIcon>
        <ListItemText primary={item.title} />
        {item.type === "collapse" && (
          item.id === selectedNav && open ? <ExpandLess /> : <ExpandMore />
        )}
      </ListItemButton>
      {drawerOpen && openDropDown === item.id && printRoutes(item)}
    </>
  );
}

CollapseNavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["item", "collapse"]).isRequired,
    url: PropTypes.string,
    icon: PropTypes.elementType,
    target: PropTypes.bool,
    external: PropTypes.bool,
    disabled: PropTypes.bool,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        childType: PropTypes.bool,
        childName: PropTypes.string,
      })
    ),
  }).isRequired,
  level: PropTypes.number.isRequired,
  userRoleRouters: PropTypes.array,
  setOpenCollapseId: PropTypes.func.isRequired,
  openCollapseId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  openDropDown: PropTypes.string,
  setOpenDropDown: PropTypes.func.isRequired,
};